import React, { useMemo, useState } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  AreaChart,
  Area,
  ComposedChart,
  Line
} from 'recharts';
import { 
  Equipment, 
  InspectionPlan, 
  InspectionActual, 
  Finding, 
  FindingLevel,
  EquipmentType,
  AppUser,
  ActivityLog,
  Frequency,
  PlanStatus
} from '../types';
import { 
  TrendingUp, 
  Zap,
  Users,
  Target,
  ShieldCheck,
  Activity,
  History,
  ShieldAlert,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Box,
  Clock,
  LayoutDashboard,
  ArrowUpRight,
  ArrowDownRight,
  MoreVertical,
  CheckCircle2
} from 'lucide-react';

interface DashboardProps {
  equipments: Equipment[];
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  findings: Finding[];
  user: AppUser;
  logs?: ActivityLog[];
}

const Dashboard: React.FC<DashboardProps> = ({ equipments, plans, actuals, findings, user, logs = [] }) => {
  // Dashboard Filter States
  const [filterMonth, setFilterMonth] = useState<number>(new Date().getMonth());
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterArea, setFilterArea] = useState<string>('ALL');

  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const assetTypes = Object.values(EquipmentType);
  const areas = useMemo(() => Array.from(new Set(equipments.map(e => e.area))), [equipments]);

  // Combined Filtering Logic
  const filtered = useMemo(() => {
    const plansInScope = plans.filter(p => {
      const pDate = new Date(p.plannedDate);
      const eq = equipments.find(e => e.id === p.equipmentId);
      if (!eq) return false;
      return pDate.getMonth() === filterMonth && 
             pDate.getFullYear() === filterYear && 
             (filterType === 'ALL' || eq.type === filterType) &&
             (filterArea === 'ALL' || eq.area === filterArea);
    });

    const actualsInScope = actuals.filter(a => {
      const aDate = new Date(a.actualDate);
      const eq = equipments.find(e => e.id === a.equipmentId);
      return aDate.getMonth() === filterMonth && 
             aDate.getFullYear() === filterYear && 
             (filterType === 'ALL' || eq?.type === filterType) &&
             (filterArea === 'ALL' || eq?.area === filterArea || a.area === filterArea);
    });

    const findingsInScope = findings.filter(f => {
      const fDate = new Date(f.date);
      return fDate.getMonth() === filterMonth && 
             fDate.getFullYear() === filterYear && 
             (filterType === 'ALL' || f.inspectionType.includes(filterType)) &&
             (filterArea === 'ALL' || f.area === filterArea);
    });

    // Calculate actual completion for the plans in scope
    const completedPlansCount = plansInScope.filter(p => 
      p.status === PlanStatus.COMPLETED || 
      actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate))
    ).length;

    return { plansInScope, actualsInScope, findingsInScope, completedPlansCount };
  }, [plans, actuals, findings, equipments, filterMonth, filterYear, filterType, filterArea]);

  const healthMetrics = useMemo(() => {
    const compliance = filtered.plansInScope.length > 0 
      ? Math.round((filtered.completedPlansCount / filtered.plansInScope.length) * 100) 
      : 100;
    
    const openFindings = filtered.findingsInScope.filter(f => f.status !== 'Closed');
    const criticalImpact = openFindings.filter(f => f.level === FindingLevel.CRITICAL).length * 15;
    const majorImpact = openFindings.filter(f => f.level === FindingLevel.MAJOR).length * 5;
    
    const score = Math.max(0, Math.min(100, compliance - (criticalImpact + majorImpact)));
    
    return { compliance, score, openFindings: openFindings.length };
  }, [filtered]);

  const trendData = useMemo(() => {
    const daysInMonth = new Date(filterYear, filterMonth + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dayStr = `${filterYear}-${String(filterMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const pCount = filtered.plansInScope.filter(p => p.plannedDate === dayStr).length;
      const aCount = filtered.actualsInScope.filter(a => a.actualDate === dayStr).length;
      const fCount = filtered.findingsInScope.filter(f => f.date === dayStr).length;
      return { name: day, planned: pCount, actual: aCount, findings: fCount };
    });
  }, [filtered, filterYear, filterMonth]);

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-20">
      {/* Executive Command Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-2 bg-white rounded-[2.5rem] border border-slate-200/60 shadow-sm">
        <div className="flex items-center gap-6 p-6">
          <div className="w-16 h-16 bg-slate-950 rounded-3xl flex items-center justify-center shadow-xl shadow-slate-950/20">
            <LayoutDashboard className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-950 tracking-tight uppercase leading-none">
              Command <span className="text-emerald-600">Center</span>
            </h1>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 flex items-center gap-2">
              <Activity className="w-3 h-3 text-emerald-500" /> EHS Operational Dashboard
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 bg-slate-50/80 lg:rounded-[2rem]">
          <FilterDropdown icon={<Calendar />} value={filterMonth} onChange={(v: string) => setFilterMonth(parseInt(v))} options={monthNames.map((m, i) => ({ label: m, value: i }))} />
          <FilterDropdown icon={<Clock />} value={filterYear} onChange={(v: string) => setFilterYear(parseInt(v))} options={years.map(y => ({ label: String(y), value: y }))} />
          <FilterDropdown icon={<Box />} value={filterType} onChange={(v: string) => setFilterType(v)} options={[{ label: 'All Assets', value: 'ALL' }, ...assetTypes.map(t => ({ label: t, value: t }))]} />
          <FilterDropdown icon={<Target />} value={filterArea} onChange={(v: string) => setFilterArea(v)} options={[{ label: 'All Areas', value: 'ALL' }, ...areas.map(a => ({ label: a, value: a }))]} />
          <button 
            onClick={() => { setFilterMonth(new Date().getMonth()); setFilterYear(currentYear); setFilterType('ALL'); setFilterArea('ALL'); }}
            className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-emerald-600 hover:border-emerald-200 transition-all active:scale-95"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <KpiCard title="Compliance" value={`${healthMetrics.compliance}%`} trend="+1.2%" isPos={true} icon={<ShieldCheck />} color="emerald" desc="Planned vs Actual execution" />
        <KpiCard title="Health Score" value={healthMetrics.score} trend="-0.4%" isPos={false} icon={<Activity />} color="indigo" desc="EHS audit readiness metric" />
        <KpiCard title="Open Findings" value={healthMetrics.openFindings} trend={filtered.findingsInScope.filter(f => f.level === FindingLevel.CRITICAL).length + " Critical"} isPos={false} icon={<AlertTriangle />} color="rose" desc="Pending corrective actions" />
        <KpiCard title="Asset Scope" value={equipments.length} trend="Active Tracking" isPos={true} icon={<Box />} color="slate" desc="Total managed safety units" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        <div className="xl:col-span-8 space-y-8">
          {/* Trend Analysis */}
          <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
              <div>
                <h3 className="text-xl font-black text-slate-950 uppercase tracking-tight flex items-center gap-3">
                  <TrendingUp className="w-6 h-6 text-emerald-500" /> Operational performance
                </h3>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">Achievement Trend • {monthNames[filterMonth]} {filterYear}</p>
              </div>
              <div className="flex items-center gap-6">
                <LegendItem color="bg-emerald-500" label="ACTUAL" />
                <LegendItem color="bg-slate-200" label="PLANNED" />
                <LegendItem color="bg-rose-500" label="FINDINGS" />
              </div>
            </div>
            
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="6 6" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 900, fill: '#94a3b8'}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 900, fill: '#cbd5e1'}} />
                  <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="planned" stroke="#e2e8f0" fill="#f8fafc" strokeWidth={2} />
                  <Area type="monotone" dataKey="actual" stroke="#10b981" fill="url(#colorActual)" strokeWidth={4} />
                  <Line type="monotone" dataKey="findings" stroke="#f43f5e" strokeWidth={3} dot={{ r: 4, fill: '#f43f5e', stroke: '#fff', strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* High Risk Area */}
          <div className="bg-slate-950 p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-16 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
               <ShieldAlert className="w-64 h-64 text-emerald-500" />
            </div>
            <div className="relative z-10 flex flex-col gap-10">
               <div className="flex justify-between items-center">
                  <h3 className="text-xl font-black text-white uppercase tracking-tighter flex items-center gap-4">
                    <Zap className="w-6 h-6 text-rose-500" /> High-Risk Intelligence
                  </h3>
                  <span className="px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-[9px] font-black text-white uppercase tracking-widest italic">Action Required</span>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filtered.findingsInScope.filter(f => f.level === FindingLevel.CRITICAL && f.status !== 'Closed').length > 0 ? (
                    filtered.findingsInScope.filter(f => f.level === FindingLevel.CRITICAL && f.status !== 'Closed').slice(0, 4).map(f => (
                      <div key={f.id} className="p-6 bg-white/5 border border-white/10 rounded-[2rem] hover:bg-white/10 transition-all flex flex-col justify-between gap-6 group/card">
                        <div>
                          <div className="flex justify-between items-start mb-4">
                             <span className="px-3 py-1 bg-rose-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest">{f.area}</span>
                             <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{f.targetDate}</span>
                          </div>
                          <p className="text-sm font-bold text-slate-100 leading-relaxed tracking-tight group-hover/card:text-emerald-400 transition-colors line-clamp-2">"{f.description}"</p>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-white/5">
                           <div className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-black">{f.pic.charAt(0)}</div>
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{f.pic}</span>
                           </div>
                           <ShieldAlert className="w-4 h-4 text-rose-500" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-16 text-center bg-white/5 border-2 border-dashed border-white/10 rounded-[2.5rem]">
                       <ShieldCheck className="w-12 h-12 text-emerald-500/20 mx-auto mb-6" />
                       <p className="text-white text-sm font-black uppercase tracking-widest leading-relaxed">No critical items detected</p>
                       <p className="text-slate-500 text-[10px] uppercase font-bold mt-2">Operational environment is currently stable</p>
                    </div>
                  )}
               </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-4 flex flex-col gap-8">
           {/* Trail */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm h-[550px] flex flex-col">
              <h3 className="text-lg font-black text-slate-950 uppercase tracking-tighter mb-10 flex items-center gap-3">
                <History className="w-6 h-6 text-emerald-500" /> System Trail
              </h3>
              <div className="flex-1 overflow-y-auto pr-3 space-y-8 custom-scrollbar">
                {logs.slice(0, 15).map(log => (
                  <div key={log.id} className="relative pl-8 border-l-2 border-emerald-50 pb-2 group">
                     <div className="absolute top-0 -left-[5px] w-2 h-2 bg-slate-200 rounded-full group-hover:bg-emerald-500 group-hover:scale-150 transition-all"></div>
                     <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {log.action}</p>
                     <p className="text-xs font-black text-slate-900 tracking-tight">{log.userName}</p>
                     <p className="text-[10px] text-slate-500 leading-relaxed mt-1 line-clamp-2 italic">"{log.details}"</p>
                  </div>
                ))}
              </div>
           </div>

           {/* Analysis */}
           <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black text-slate-950 uppercase tracking-tighter mb-8 flex items-center gap-3">
                <Activity className="w-6 h-6 text-indigo-500" /> Severity Analysis
              </h3>
              <div className="space-y-6">
                 <AnalysisBar label="Critical" count={filtered.findingsInScope.filter(f => f.level === FindingLevel.CRITICAL).length} total={filtered.findingsInScope.length || 1} color="bg-rose-500" />
                 <AnalysisBar label="Major" count={filtered.findingsInScope.filter(f => f.level === FindingLevel.MAJOR).length} total={filtered.findingsInScope.length || 1} color="bg-orange-500" />
                 <AnalysisBar label="Minor" count={filtered.findingsInScope.filter(f => f.level === FindingLevel.MINOR).length} total={filtered.findingsInScope.length || 1} color="bg-indigo-500" />
                 <AnalysisBar label="Observation" count={filtered.findingsInScope.filter(f => f.level === FindingLevel.OBSERVATION).length} total={filtered.findingsInScope.length || 1} color="bg-slate-300" />
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, trend, isPos, icon, color, desc }: any) => {
  const colors: any = { emerald: 'bg-emerald-50 border-emerald-100', indigo: 'bg-indigo-50 border-indigo-100', rose: 'bg-rose-50 border-rose-100', slate: 'bg-slate-50 border-slate-100' };
  return (
    <div className={`p-8 rounded-[2.5rem] border ${colors[color]} hover:shadow-2xl transition-all duration-300 group`}>
      <div className="flex justify-between items-center mb-8">
        <div className="p-4 bg-white rounded-2xl shadow-sm text-slate-900 group-hover:rotate-6 transition-transform">{icon}</div>
        <div className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-wide ${isPos ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>{trend}</div>
      </div>
      <h4 className="text-4xl font-black text-slate-950 tracking-tighter leading-none">{value}</h4>
      <p className="text-[10px] font-black text-slate-900 mt-2 uppercase tracking-widest">{title}</p>
      <p className="text-[10px] text-slate-400 font-bold mt-1 uppercase opacity-60 flex items-center gap-2"><MoreVertical className="w-3 h-3" /> {desc}</p>
    </div>
  );
};

const FilterDropdown = ({ icon, value, onChange, options }: any) => (
  <div className="relative group">
    <div className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-emerald-500 pointer-events-none">{React.cloneElement(icon, { className: 'w-4 h-4' })}</div>
    <select value={value} onChange={e => onChange(e.target.value)} className="pl-14 pr-10 py-4 bg-white/50 border border-transparent rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none hover:bg-white hover:border-slate-200 transition-all appearance-none cursor-pointer focus:ring-4 focus:ring-emerald-500/10 min-w-[140px]">
      {options.map((opt: any) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

const LegendItem = ({ color, label }: any) => (
  <div className="flex items-center gap-2">
    <div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm shadow-black/10`}></div>
    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
  </div>
);

const AnalysisBar = ({ label, count, total, color }: any) => {
  const percent = Math.round((count / total) * 100);
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end"><span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{label}</span><span className="text-xs font-black text-slate-950">{count}</span></div>
      <div className="h-2.5 bg-slate-50 rounded-full overflow-hidden border border-slate-100 p-0.5">
        <div className={`h-full ${color} rounded-full transition-all duration-1000`} style={{ width: `${percent}%` }}></div>
      </div>
    </div>
  );
};

export default Dashboard;
